import React, { useState, useEffect, useRef, useCallback } from "react";
import darkModeIcon from "./darkBT.gif";
import logoQWER from "./log_QWER.jpeg";
import timelineProfile from "./timeline-profile.jpg";
import endGif1 from "./end1.gif";
import endGif2 from "./end2.gif";
import endGif3 from "./end3.gif";
import { useTranslation } from "react-i18next";
import i18n from "../src/lang/i18n";
import "./TestTimeline.css";

const extractYouTubeID = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  return dateString.split("T")[0];
};

const isMobile = () => {
  return /Mobi|Android|iPhone/i.test(navigator.userAgent);
};

const getRandomEndGif = () => {
  const gifs = [endGif1, endGif2, endGif3];
  return gifs[Math.floor(Math.random() * gifs.length)];
};

const Broadcast = ({
  darkMode,
  toggleDarkMode,
  language,
  langbt,
  onChangeLangKO,
  onChangeLangENG,
  onChangeLangJP,
}) => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const loader = useRef(null);
  const scrollTimeout = useRef(null);
  const [endGif, setEndGif] = useState(getRandomEndGif());
  const [selectedEvent, setSelectedEvent] = useState("");
  const [eventList, setEventList] = useState([]);
  const [sortOrder, setSortOrder] = useState("ASC");
  const [isEventListOpen, setIsEventListOpen] = useState(false);

  // 이미지 로딩 최적화를 위한 상태 추가
  const [loadedImages, setLoadedImages] = useState(new Set());
  const imageObserver = useRef(null);

  // 이미지 지연 로딩을 위한 Intersection Observer 설정
  useEffect(() => {
    imageObserver.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.getAttribute("data-src");
            if (src) {
              img.src = src;
              img.removeAttribute("data-src");
              setLoadedImages((prev) => new Set([...prev, src]));
            }
            imageObserver.current.unobserve(img);
          }
        });
      },
      {
        rootMargin: "50px 0px",
        threshold: 0.1,
      },
    );

    return () => {
      if (imageObserver.current) {
        imageObserver.current.disconnect();
      }
    };
  }, []);

  // 이미지 요소 참조를 위한 함수
  const imageRef = useCallback((node) => {
    if (node && imageObserver.current) {
      imageObserver.current.observe(node);
    }
  }, []);

  // 서버에서 데이터를 가져오는 함수
  const fetchData = async (
    pageNum = 1,
    sortOrderParam = "ASC",
    eventFilter = "",
  ) => {
    setIsLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
      let url = `${baseUrl}/army/kforce?page=${pageNum}&sortBy=uploadDate:${sortOrderParam}`;

      if (eventFilter) {
        url += `&tag=${eventFilter}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();

      if (pageNum === 1) {
        setItems(result.data);
        setAllItems(result.data);
      } else {
        setItems((prev) => [...prev, ...result.data]);
      }

      setPage(result.meta.currentPage);
      setTotalPages(result.meta.totalPages);
      setHasMore(result.meta.currentPage < result.meta.totalPages);
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData(1, sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    const fetchEventList = async () => {
      try {
        const response = await fetch("/eventList.json");
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setEventList(data);
      } catch (error) {
        console.error("Error loading event list:", error);
      }
    };
    fetchEventList();
  }, []);

  const loadMoreItems = async () => {
    if (isLoading || !hasMore) return;

    const nextPage = page + 1;

    setIsLoading(true);
    try {
      // 모바일에서는 더 짧은 지연 시간 사용
      const delay = isMobile() ? 150 : 300;
      await new Promise((resolve) => setTimeout(resolve, delay));

      await fetchData(nextPage, sortOrder, selectedEvent);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScroll = () => {
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    const debounceTime = isMobile() ? 100 : 150;
    scrollTimeout.current = setTimeout(() => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      let scrollThreshold;
      if (isMobile()) {
        scrollThreshold = documentHeight * 0.5; // 모바일은 50% 지점
      } else {
        scrollThreshold = documentHeight * 0.8; // 데스크탑은 80% 지점
      }

      if (scrollTop >= scrollThreshold) {
        loadMoreItems();
      }
    }, debounceTime);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [isLoading, items, hasMore]);

  const getYouTubeAppLink = (url) => {
    const videoId = extractYouTubeID(url);
    if (!videoId) return url;
    if (/Android/i.test(navigator.userAgent)) {
      return `intent://www.youtube.com/watch?v=${videoId}#Intent;package=com.google.android.youtube;scheme=https;end`;
    }
    return `https://www.youtube.com/watch?v=${videoId}`;
  };

  const handleThumbnailClick = (url) => {
    const link = isMobile() ? getYouTubeAppLink(url) : url;
    window.open(link, "_blank");
  };

  const handleEventFilter = (event) => {
    setSelectedEvent(event);
    fetchData(1, sortOrder, event);
  };

  const handleSort = () => {
    const newSortOrder = sortOrder === "ASC" ? "DESC" : "ASC";
    setSortOrder(newSortOrder);
    fetchData(1, newSortOrder, selectedEvent);
  };

  return (
    <div className={`Broadcast ${darkMode ? "dark-mode" : ""}`}>
      <h1>{language("ROKA")}</h1>
      <h3>{language("ROKA_sub")}</h3>

      {/* <button className="toggle-button" onClick={toggleDarkMode}>
        <div className="bwicon"></div>
        <span>{language("switMode")}</span>
      </button> */}
      <div class="lang-switch">
        <button
          className={`lang-btn ${i18n.language === "ko" ? "active" : ""}`}
          onClick={langbt.ko}
        >
          KR
        </button>
        <button
          className={`lang-btn ${i18n.language === "en" ? "active" : ""}`}
          onClick={langbt.en}
        >
          EN
        </button>
        <button
          className={`lang-btn ${i18n.language === "jp" ? "active" : ""}`}
          onClick={langbt.jp}
        >
          JP
        </button>
      </div>
      <button className="sort-button" onClick={handleSort}>
        <span className="control-desktop-label">{sortOrder === "ASC" ? language("sortNew") : language("sortOld")}</span>
        <span className="control-mobile-label">{language("sortLabel")}: {sortOrder === "ASC" ? language("orderOld") : language("orderNew")}</span>
      </button>
      <div className="test-timeline">
        {items.map((item, index) => {
          const videoId = extractYouTubeID(item.url);
          const thumbnailUrl =
            item.iconImg ||
            (videoId ? `https://img.youtube.com/vi/${videoId}/0.jpg` : "");
          return (
            <div
              className={`test-timeline-item ${
                index % 2 === 0 ? "left" : "right"
              }`}
              key={item.id}
            >
              <div className="test-timeline-circle">
                <img
                  src={timelineProfile}
                  alt="QWER Logo"
                  className="test-timeline-circle-img"
                />
              </div>
              <div className="test-timeline-card">
                {thumbnailUrl && (
                  <img
                    src={thumbnailUrl}
                    alt={item.title}
                    className="thumbnail"
                    onClick={() => handleThumbnailClick(item.url)}
                  />
                )}
                <div className="test-timeline-title-container">
                  <h2>{item.title}</h2>
                </div>
                <div className="test-timeline-date">
                  {formatDate(item.uploadDate)}
                </div>
                <a
                  href="https://www.youtube.com/@we_nal/featured"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="channel-label"
                >
                  {language("ch")}: 위날 we nal
                </a>
                <a
                  href={isMobile() ? getYouTubeAppLink(item.url) : item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="test-timeline-link"
                >
                  {language("watch")}
                </a>
              </div>
            </div>
          );
        })}
        <div ref={loader} className="loader">
          {isLoading && (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>{language("loading")}</p>
            </div>
          )}
        </div>
      </div>
      <footer className="footer">{language("footer")}</footer>
    </div>
  );
};

export default Broadcast;
